import { FIRST_PARTY_HOST_PATTERN } from "@/lib/errors/no-first-party-frame-fingerprint/constants";

/** `at <fn> @ <url>:<line>:<column>`, capturing the URL. */
const FRAME_URL_PATTERN = / @ (https?:\/\/\S+?):\d+:\d+$/;

/** Paths that serve script files rather than page documents. */
const SCRIPT_PATH_PATTERN = /^\/_next\/|\.m?js$/;

/**
 * Detects "Maximum call stack size exceeded" errors thrown by script the
 * browser evaluated into the page on mobile WebKit (iOS Safari and Chrome),
 * where the call stack limit is much smaller than on desktop. These are not
 * actionable.
 *
 * Matches when:
 * 1. The error message is "Maximum call stack size exceeded"
 * 2. Every frame is either URL-less (`@ undefined`) or points at one of our
 *    page documents rather than a script file. We serve no inline scripts, so
 *    a document frame is always injected. Chrome for iOS's built-in Google
 *    Translate reports its frames this way
 *    (`at xk @ https://app.marblehealth.com/portal/appointments:230:383`).
 *
 * A frame in our `/_next/static/` bundle, or in any script file, keeps the
 * error reportable.
 */
export const isMobileStackOverflowNoise = (
  message: string | undefined,
  stack: string | undefined,
): boolean => {
  if (!message?.includes("Maximum call stack size exceeded")) {
    return false;
  }

  if (!stack) {
    return true;
  }

  const frames = stack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "));

  if (frames.length === 0) {
    return true;
  }

  return frames.every((frame) => {
    const trimmed = frame.trim();
    return trimmed.includes("@ undefined") || _isPageDocumentFrame(trimmed);
  });
};

function _isPageDocumentFrame(frame: string): boolean {
  const url = FRAME_URL_PATTERN.exec(frame)?.[1];
  if (!url) {
    return false;
  }

  try {
    const { hostname, pathname } = new URL(url);
    return (
      FIRST_PARTY_HOST_PATTERN.test(hostname) &&
      !SCRIPT_PATH_PATTERN.test(pathname)
    );
  } catch {
    return false;
  }
}
